import {
  type FunctionComponent,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import { Button, Checkbox } from '@dr.pogodin/react-utils';
import { captureException } from '@sentry/react';

import Throbber from 'components/Throbber';

import useUserArtist from 'hooks/useUserArtist';

import { updateArtist } from 'services/artists';

import { type ModalityT, getId } from 'utils';

import secondaryButtonTheme from 'themes/Button/admin/secondary-v2.scss';

import type { TabRefT } from '../common';

import './style.scss';

type PropsT = {
  goToNextTab: () => void;
  goToPrevTab: () => void;
  ref: Ref<TabRefT>;
};

const Tab02: FunctionComponent<PropsT> = ({
  goToNextTab,
  goToPrevTab,
  ref,
}) => {
  const practitioner = useUserArtist();

  const m = practitioner.data?.modalities;

  // TODO: This is not the best organization... we should do something
  // more maintainable and less error prone.

  const [yoga, setYoga] = useState<boolean>(!!m?.includes('Yoga'));
  const [pilates, setPilates] = useState<boolean>(!!m?.includes('Pilates'));

  const [breathwork, setBreathwork] = useState<boolean>(
    !!m?.includes('Breathwork'),
  );

  const [calisthenics, setCalisthenics] = useState<boolean>(
    !!m?.includes('Calisthenics'),
  );

  const [meditation, setMeditation] = useState<boolean>(
    !!m?.includes('Meditation'),
  );

  const [soundbath, setSoundbath] = useState<boolean>(
    !!m?.includes('Soundbath'),
  );

  const [somaticMovement, setSomaticMovement] = useState<boolean>(
    !!m?.includes('Somatic Movement'),
  );

  const [qiGongTaiChi, setQiGongTaiChi] = useState<boolean>(
    !!m?.includes('QiGong/Tai Chi'),
  );

  const [functionalMovement, setFunctionalMovement] = useState<boolean>(
    !!m?.includes('Functional Movement'),
  );

  const [other, setOther] = useState<boolean>(
    !!m?.includes('Other'),
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setYoga(!!m?.includes('Yoga'));
    setPilates(!!m?.includes('Pilates'));
    setBreathwork(!!m?.includes('Breathwork'));
    setCalisthenics(!!m?.includes('Calisthenics'));
    setMeditation(!!m?.includes('Meditation'));
    setSoundbath(!!m?.includes('Soundbath'));
    setSomaticMovement(!!m?.includes('Somatic Movement'));
    setQiGongTaiChi(!!m?.includes('QiGong/Tai Chi'));
    setFunctionalMovement(!!m?.includes('Functional Movement'));
    setOther(!!m?.includes('Other'));
  }, [m]);

  const [busy, setBusy] = useState<boolean>(false);

  const save = useCallback(async () => {
    try {
      setBusy(true);

      const upd: {
        customModalities: string[];
        modalities: ModalityT[];
      } = {
        customModalities: [],
        modalities: [],
      };

      if (yoga) upd.modalities.push('Yoga');
      if (pilates) upd.modalities.push('Pilates');
      if (breathwork) upd.modalities.push('Breathwork');
      if (calisthenics) upd.modalities.push('Calisthenics');
      if (meditation) upd.modalities.push('Meditation');
      if (soundbath) upd.modalities.push('Soundbath');
      if (somaticMovement) upd.modalities.push('Somatic Movement');
      if (qiGongTaiChi) upd.modalities.push('QiGong/Tai Chi');
      if (functionalMovement) upd.modalities.push('Functional Movement');
      if (other) upd.modalities.push('Other');

      const a = await updateArtist(getId(practitioner.data), upd);
      await practitioner.reload(() => a);
    } catch (error) {
      captureException(error);
      // eslint-disable-next-line no-alert
      alert('Failed to save data');
    }

    setBusy(false);
  }, [
    breathwork,
    calisthenics,
    functionalMovement,
    meditation,
    other,
    pilates,
    practitioner,
    qiGongTaiChi,
    somaticMovement,
    soundbath,
    yoga,
  ]);

  useImperativeHandle(ref, () => ({
    save,
    valid: true,
  }), [save]);

  return (
    <div styleName="container">
      <p>
        Choose the practices that feel most authentic to your teaching and
        approach to wellness.
      </p>
      <h2>Select all that apply</h2>
      <div styleName="grid">
        <Checkbox
          checked={yoga}
          label="Yoga"
          onChange={() => {
            setYoga(!yoga);
          }}
        />
        <Checkbox
          checked={meditation}
          label="Meditation"
          onChange={() => {
            setMeditation(!meditation);
          }}
        />
        <Checkbox
          checked={breathwork}
          label="Breathwork"
          onChange={() => {
            setBreathwork(!breathwork);
          }}
        />
        <Checkbox
          checked={somaticMovement}
          label="Somatic Movement"
          onChange={() => {
            setSomaticMovement(!somaticMovement);
          }}
        />
        <Checkbox
          checked={soundbath}
          label="Soundbath"
          onChange={() => {
            setSoundbath(!soundbath);
          }}
        />
        <Checkbox
          checked={functionalMovement}
          label="Functional Movement"
          onChange={() => {
            setFunctionalMovement(!functionalMovement);
          }}
        />
        <Checkbox
          checked={qiGongTaiChi}
          label="QiGong/Tai Chi"
          onChange={() => {
            setQiGongTaiChi(!qiGongTaiChi);
          }}
        />
        <Checkbox
          checked={calisthenics}
          label="Calisthenics"
          onChange={() => {
            setCalisthenics(!calisthenics);
          }}
        />
        <Checkbox
          checked={pilates}
          label="Pilates"
          onChange={() => {
            setPilates(!pilates);
          }}
        />
        <Checkbox
          checked={other}
          label="Other"
          onChange={() => {
            setOther(!other);
          }}
        />
      </div>
      <div styleName="hr" />
      <div styleName="buttons">
        <Button
          onClick={() => {
            void (async () => {
              await save();
              goToPrevTab();
            })();
          }}
          theme={secondaryButtonTheme}
        >
          Previous
        </Button>
        <Button
          onClick={() => {
            void (async () => {
              await save();
              goToNextTab();
            })();
          }}
        >
          Next
        </Button>
      </div>
      {busy ? <Throbber withOverlay /> : null}
    </div>
  );
};

export default Tab02;
